import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { projects } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import AnimatedBackground from '../components/AnimatedBackground';
import { localizedPath, sectionId } from '../utils/routes';
import { SEO } from '../components/SEO';
import { BeforeAfterGallery } from '../components/BeforeAfterSlider';

const CaseStudyPage = () => {
  const { t, i18n } = useTranslation();
  const { slug, lang } = useParams();
  const currentLang = lang || 'it';
  const navigate = useNavigate();
  
  const project = projects.find(p => p.slug === slug);
  
  if (!project) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <AnimatedBackground />
        <div className="text-center relative z-10">
          <h1 className="text-white text-4xl mb-4">{t('case_study.not_found')}</h1>
          <Link to={localizedPath(currentLang, 'home')} className="text-[#FFD700]">{t('case_study.go_home')}</Link>
        </div>
      </div>
    );
  }

  // Get translated project data with fallback to mock data
  const tp = (field) => {
    const key = `case_study.projects.${project.id}.${field}`;
    const translated = t(key, { returnObjects: true, defaultValue: null });
    if (translated && translated !== key) return translated;
    return project[field];
  };

  const handleGoToContact = () => {
    navigate(localizedPath(currentLang, 'home'));
    setTimeout(() => {
      const el = document.getElementById(sectionId(currentLang, 'contact'));
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }, 500);
  };

  const handleGoToPortfolio = () => {
    navigate(localizedPath(currentLang, 'home'));
    setTimeout(() => {
      const el = document.getElementById(sectionId(currentLang, 'portfolio'));
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }, 500);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO page="project" projectTitle={tp('title')} path={localizedPath(currentLang, 'project', { slug })} />
      <CustomCursor />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background image with overlay - hidden when project has its own client logo to avoid visual overlap */}
        {!project.clientLogo && (
          <div className="absolute inset-0">
            <img 
              src={project.image} 
              alt={project.title}
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/90 to-[#0a0a0a]" />
          </div>
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          {/* Back button */}
          <motion.button
            onClick={() => navigate(localizedPath(currentLang, 'home'))}
            className="flex items-center gap-2 text-white/60 hover:text-[#FFD700] transition-colors mb-12"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-xs tracking-[0.2em] uppercase">{t('case_study.back')}</span>
          </motion.button>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Title section */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {project.isRealProject && (
                <span className="inline-block bg-[#FFD700] text-black text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 font-semibold mb-6">
                  {t('case_study.real_project')}
                </span>
              )}
              {project.clientLogo && (
                <img
                  src={project.clientLogo}
                  alt={`${project.title} logo`}
                  className="block h-20 md:h-24 w-auto max-w-[280px] object-contain object-left mb-6"
                  data-testid="case-study-client-logo"
                />
              )}
              
              <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-4 block">
                {project.category}
              </span>
              
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95] mb-6">
                {tp('title')}
              </h1>
              
              <p className="text-white/80 text-xl font-light leading-relaxed">
                {tp('description')}
              </p>
            </motion.div>

            {/* Project info box */}
            <motion.div
              className="bg-white/5 backdrop-blur-sm border border-white/10 p-8"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="text-white/30 text-xs tracking-[0.3em] uppercase mb-6">{t('case_study.project_details')}</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-white/50 text-sm">{t('case_study.client')}</span>
                  <span className="text-white font-medium">{project.client}</span>
                </div>
                
                {project.year && (
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-white/50 text-sm">{t('case_study.year')}</span>
                    <span className="text-white font-medium">{project.year}</span>
                  </div>
                )}
                
                {project.sector && (
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-white/50 text-sm">{t('case_study.sector')}</span>
                    <span className="text-white font-medium">{tp('sector')}</span>
                  </div>
                )}
                
                {project.services && (
                  <div className="py-3">
                    <span className="text-white/50 text-sm block mb-3">{t('case_study.services')}</span>
                    <div className="flex flex-wrap gap-2">
                      {(tp('services') || []).map((service) => (
                        <span 
                          key={service}
                          className="text-[10px] tracking-[0.15em] uppercase text-[#FFD700] border border-[#FFD700]/30 px-3 py-1.5"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {project.link && (
                <motion.a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="elegant-button w-full mt-8 py-4 text-xs tracking-[0.3em] uppercase flex items-center justify-center gap-3"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>{t('case_study.visit_site')}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </motion.a>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Image */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="relative aspect-video overflow-hidden bg-black/50 rounded-lg"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src={project.image} 
              alt={project.title}
              className="w-full h-full object-contain"
            />
          </motion.div>
        </div>
      </section>

      {/* Full Description */}
      {project.fullDescription && (
        <section className="py-20 relative z-10">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6">{t('case_study.the_project')}</h2>
              <p className="text-white/70 text-xl leading-relaxed font-light">
                {tp('fullDescription')}
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Before / After Gallery */}
      {project.beforeAfter && project.beforeAfter.length > 0 && (
        <BeforeAfterGallery
          items={project.beforeAfter.map((ba) => ({
            ...ba,
            label: t(`case_study.before_after.tabs.${ba.key}`),
          }))}
          beforeLabel={t('case_study.before_after.before')}
          afterLabel={t('case_study.before_after.after')}
          subtitle={t('case_study.before_after.subtitle')}
          title={t('case_study.before_after.title')}
          description={t('case_study.before_after.description')}
        />
      )}

      {/* Logo / Brand Identity Variants */}
      {project.logoVariants && project.logoVariants.length > 0 && (
        <section className="py-20 relative z-10" data-testid="brand-identity-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-4">
                {t('case_study.brand_identity.subtitle')}
              </h2>
              <h3 className="font-display text-4xl md:text-5xl text-white leading-tight mb-10">
                {project.brandIdentityTitle || t('case_study.brand_identity.title')}
              </h3>

              <div className={`grid gap-5 max-w-3xl ${
                project.logoVariants.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' :
                project.logoVariants.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
              }`}>
                {project.logoVariants.map((variant, idx) => {
                  // First variant = main logo (larger), subsequent = mark/favicon (smaller)
                  const isMain = idx === 0;
                  const sizingClass = isMain
                    ? 'max-w-[88%] max-h-[78%]'
                    : 'max-w-[40%] max-h-[40%]';
                  return (
                    <motion.div
                      key={variant.src}
                      className="relative aspect-[4/3] flex items-center justify-center p-6"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      data-testid={`logo-variant-${idx}`}
                    >
                      <img
                        src={variant.src}
                        alt={`${project.title} - ${variant.label}`}
                        className={`${sizingClass} object-contain`}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Features */}
      {project.features && (
        <section className="py-20 relative z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-12">{t('case_study.includes')}</h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(tp('features') || []).map((feature, idx) => (
                  <motion.div
                    key={feature}
                    className="bg-white/5 border border-white/10 p-6 group hover:border-[#FFD700]/50 transition-colors duration-500"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-[#FFD700] text-2xl font-display">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <p className="text-white/70 group-hover:text-white transition-colors">
                        {feature}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Closing Quote */}
      {project.closingQuote && (
        <section className="py-32 relative z-10">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-16 h-px bg-[#FFD700] mx-auto mb-12" />
              <p className="font-serif italic text-white/60 text-2xl md:text-3xl leading-relaxed">
                "{tp('closingQuote')}"
              </p>
              <div className="w-16 h-px bg-[#FFD700] mx-auto mt-12" />
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 border-t border-white/10 relative z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-display text-4xl md:text-5xl text-white mb-6">
              {t('case_study.cta_title')}
            </h2>
            <p className="text-white/50 text-lg mb-10">
              {t('case_study.cta_description')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.button
                onClick={handleGoToContact}
                className="elegant-button px-10 py-4 text-xs tracking-[0.3em] uppercase"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {t('case_study.cta_button')}
              </motion.button>
              <motion.button
                onClick={handleGoToPortfolio}
                className="text-white/50 hover:text-white text-xs tracking-[0.3em] uppercase transition-colors flex items-center gap-2"
                whileHover={{ x: 5 }}
              >
                <span>{t('case_study.other_projects')}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudyPage;
